import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { createUUID, getItemLocal, setItemLocal } from "@miplatform/utils";
import { ACTIVE_STEP } from "../../constants";

const UPDATE_AREA_ID = createUUID();

export const useAddSpecSheetViewModel = ({ setRequiredCategoriesMatch }) => {
  const [planDetails, setPlanDetails] = useState({});
  const [priceRollup, setPriceRollup] = useState({});
  const [areaTypes, setAreaTypes] = useState([]);
  const [areaTemplates, setAreaTemplates] = useState([]);

  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const GET_PRICE_ROLLUP_UUID = createUUID();
  const GET_AREA_TYPES_UUID = createUUID();
  const GET_AREA_TEMPLATES_UUID = createUUID();
  const GET_PLAN_DETAILS_UUID = createUUID();
  const HANDLE_CREATE_SPEC_PLAN_UUID = createUUID();
  const HANDLE_ADD_AREAS_UUID = createUUID();
  const HANDLE_ADD_ITEMS_UUID = createUUID();

  const generateAreaDisplayOrder = (areas) => {
    const sortedAreas = areas.sort(
      (a, b) =>
        (a.specColumn ?? a.areaType.specColumn) -
        (b.specColumn ?? b.areaType.specColumn)
    );
    return sortedAreas.map((area, index) => ({
      ...area,
      displayOrder: index + 1,
    }));
  };

  const getTotalFloors = (areas = []) => {
    const floors =
      areas.length > 0 ? new Set(areas.map((area) => area.floor)) : new Set();
    return floors.size;
  };

  const getPriceRollup = async (id) => {
    stateManager.abort(GET_PRICE_ROLLUP_UUID);
    stateManager.run(async () => {
      const response = await homeService.pricing.plans.getById({ id });
      if (response.status === HTTP_STATUSES.OK) {
        setPriceRollup(response.data);
      }
    }, GET_PRICE_ROLLUP_UUID);
  };

  const getAreaTypes = async () => {
    stateManager.abort(GET_AREA_TYPES_UUID);
    stateManager.run(async () => {
      const response = await homeService.areaTypes.getAll({ sort: "name" });
      if (response.status === HTTP_STATUSES.OK) {
        setAreaTypes(response.data);
      }
    }, GET_AREA_TYPES_UUID);
  };

  const getAreaTemplates = () => {
    stateManager.abort(GET_AREA_TEMPLATES_UUID);
    stateManager.run(async () => {
      const response = await homeService.plans.getAreaTemplates();
      if (response.status === HTTP_STATUSES.OK) {
        setAreaTemplates(response.data);
      }
    }, GET_AREA_TEMPLATES_UUID);
  };

  const handleCreateSpecPlan = (data) => {
    const modifiedData = data || planDetails;
    modifiedData.totalFloors = getTotalFloors(modifiedData.planAreas);

    stateManager.abort(HANDLE_CREATE_SPEC_PLAN_UUID);
    return stateManager.run(async () => {
      const response = await homeService.plans.update({
        data: modifiedData,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setPlanDetails(response.data);
      }
      return response;
    }, HANDLE_CREATE_SPEC_PLAN_UUID);
  };

  const getPlanDetails = (id, activeStep) => {
    stateManager.abort(GET_PLAN_DETAILS_UUID);
    stateManager.run(async () => {
      const response = await homeService.plans.getById({ id });
      if (response.status === HTTP_STATUSES.OK) {
        setPlanDetails(response.data);
        if (activeStep !== ACTIVE_STEP.DETAILS) {
          getPriceRollup(response.data.id);
        }
      }
    }, GET_PLAN_DETAILS_UUID);
  };

  const updateLocalArea = (id, data) => {
    const areaIndex = planDetails.planAreas.findIndex((area) => area.id === id);
    const updatedPlan = { ...planDetails };
    updatedPlan.planAreas[areaIndex] = {
      ...updatedPlan.planAreas[areaIndex],
      ...data,
    };
    return updatedPlan;
  };

  const handleUpdateArea = (id, data) => {
    stateManager.abort(UPDATE_AREA_ID);
    stateManager.run(async (signal) => {
      const plan = updateLocalArea(id, data);
      plan.totalFloors = getTotalFloors(plan.planAreas);

      const response = await homeService.plans.update({
        data: plan,
        signal,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setPlanDetails(response.data);
        getPriceRollup(response.data.id);
      }
    }, UPDATE_AREA_ID);
  };

  const handleAddAreas = async (areas, replaceExisting = false) => {
    const plan = { ...planDetails };

    if (replaceExisting) {
      plan.planAreas = [];
    }

    const areaData = areas.map(({ areaTypeId, floorId, label, specColumn }) => {
      const copies = plan.planAreas.filter((area) =>
        area.label.includes(label)
      );
      const copyNumber = copies.length;

      const getLabel = () => {
        const baseLabel = label;
        const isDuplicate = plan.planAreas.some((a) => a.label === baseLabel);

        if (copyNumber === 0) {
          return baseLabel;
        }
        if (isDuplicate) {
          return `${baseLabel} (Copy ${copyNumber})`;
        }
        return baseLabel;
      };

      return {
        areaTypeId,
        floorId,
        specColumn,
        label: getLabel(),
      };
    });

    const updatedPlanAreas = generateAreaDisplayOrder([
      ...plan.planAreas,
      ...areaData,
    ]);

    stateManager.abort(HANDLE_ADD_AREAS_UUID);
    return stateManager.run(async () => {
      const updatedPlan = {
        ...plan,
        planAreas: updatedPlanAreas,
      };
      updatedPlan.totalFloors = getTotalFloors(updatedPlanAreas);

      const response = await homeService.plans.update({ data: updatedPlan });

      if (response.status === HTTP_STATUSES.OK) {
        setPlanDetails(response.data);
      }
    }, HANDLE_ADD_AREAS_UUID);
  };

  const handleRemoveArea = (area) => {
    const updatedPlanAreas = generateAreaDisplayOrder(
      planDetails.planAreas.filter((planArea) => planArea.id !== area.id)
    );
    const plan = { ...planDetails, planAreas: updatedPlanAreas };
    handleCreateSpecPlan(plan);

    removeAreaFromLocalStorage([area.id]);
    setRequiredCategoriesMatch((prev) => {
      const updated = { ...prev };
      delete updated[area.id];
      return updated;
    });
  };

  const handleRemoveFloor = (floorToRemove) => {
    const removedAreas = [];
    const updatedPlanAreas = planDetails.planAreas.filter((area) => {
      if (area.floorId === floorToRemove.id) {
        removedAreas.push(area.id);
      }
      return area.floorId !== floorToRemove.id;
    });

    const updatedPlanDetails = {
      ...planDetails,
      planAreas: updatedPlanAreas,
    };

    removeAreaFromLocalStorage(removedAreas);
    return handleCreateSpecPlan(updatedPlanDetails);
  };

  const handleAddItems = async (updatedAreas) => {
    let plan = { ...planDetails };

    await updatedAreas.reduce(async (prevPromise, updatedArea) => {
      await prevPromise;
      stateManager.abort(HANDLE_ADD_ITEMS_UUID);
      return stateManager.run(async () => {
        const updatedPlanAreasWithItems = plan.planAreas.map((area) => {
          if (area.id === updatedArea.id) {
            return { ...area, materials: updatedArea.materials };
          }
          return area;
        });

        const updatedPlan = {
          ...plan,
          planAreas: updatedPlanAreasWithItems,
        };
        updatedPlan.totalFloors = getTotalFloors(updatedPlanAreasWithItems);

        const response = await homeService.plans.update({
          data: updatedPlan,
        });

        if (response.status === HTTP_STATUSES.OK) {
          plan = response.data;
          setPlanDetails(response.data);
        }
      }, HANDLE_ADD_ITEMS_UUID);
    }, Promise.resolve());

    if (plan?.id) {
      getPriceRollup(plan.id);
    }
  };

  const removeAreaFromLocalStorage = (areaIds = []) => {
    const updatedDismissedCategories = getItemLocal(planDetails?.id) || {};
    areaIds.forEach((areaId) => {
      if (updatedDismissedCategories[areaId]) {
        delete updatedDismissedCategories[areaId];
      }
    });
    setItemLocal(planDetails?.id, updatedDismissedCategories);
  };

  return {
    handleCreateSpecPlan,
    stateManager,
    isLoading,
    planDetails,
    setPlanDetails,
    getPlanDetails,
    areaTypes,
    getAreaTypes,
    areaTemplates,
    getAreaTemplates,
    priceRollup,
    getPriceRollup,
    handleUpdateArea,
    handleAddAreas,
    handleRemoveArea,
    handleAddItems,
    handleRemoveFloor,
  };
};
